import { useState, useEffect } from 'react';
import Logger from '@lmig/dotcom-utilities-helpers/logger';
import useEnhancedEffect from '@lmig/dotcom-aspect-hooks/useEnhancedEffect.ts';

const logger = new Logger('client:hooks:useLocalStorage');

const getValue = key => {
  let item = null;

  try {
    item = window.localStorage.getItem(key);

    return JSON.parse(item);
  } catch (error) {
    if (error.message !== 'window is not defined') {
      logger.warn(`unable to parse localStorage item with key: ${key}, and error: ${error.message}`);
    }

    return item;
  }
};

/**
 * Allows state values to be persisted to localStorage
 *
 * @param {string} key Which key is used to store the value in localStorage
 * @param {*} initialValue Any JSON serializable data type
 * @param {boolean} syncValue Boolean to indicate whether or not to sync stored value with initialValue
 */
const useLocalStorage = (key, initialValue, syncValue = true) => {
  const [storedValue, setStoredValue] = useState(() => {
    const valueFromStorage = getValue(key);

    if (valueFromStorage) {
      return valueFromStorage;
    }

    return initialValue instanceof Function ? initialValue() : initialValue;
  });

  useEffect(() => {
    window.addEventListener('storage', event => {
      if (event.key === key) {
        setStoredValue(getValue(key));
      }
    });

    return () => {
      window.removeEventListener("storage", () => { });
    };
  }, [key]);

  // Since this can server render with the initial value, we want to be sure
  //   that when we've reached the client we update state with the latest value
  //   from the storage key.
  useEnhancedEffect(() => {
    const valueOnHydration = getValue(key);

    if (valueOnHydration) {
      setStoredValue(valueOnHydration);
    }
  }, []);

  // Keep the localStorage key in sync with the local state
  useEffect(() => {
    if (syncValue) {
      try {
        window.localStorage.setItem(key, JSON.stringify(storedValue));
      } catch (error) {
        logger.warn(`unable to parse localStorage item with key: ${key}, and error: ${error.message}`);
      }
    }
  }, [key, storedValue, syncValue]);

  return [storedValue, setStoredValue];
};

export default useLocalStorage;
